
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format } from "date-fns";
import { User, Phone, Mail, CalendarDays, Clock, MessageSquare, Send, Check } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

const timeSlots = [
  { id: "morning", label: "Morning (9 AM - 12 PM)" },
  { id: "evening", label: "Evening (3 PM - 7 PM)" },
];

const treatmentOptions = [
  { id: "scaling", label: "Scaling" },
  { id: "braces", label: "Braces" },
  { id: "implants", label: "Implants" },
  { id: "rootcanal", label: "Root Canal" },
  { id: "whitening", label: "Whitening" },
  { id: "checkup", label: "Checkup" },
];

// Form validation schema
const formSchema = z.object({
  bookingPersonName: z.string().min(2, { message: "Name must be at least 2 characters." }),
  bookingPersonPhone: z.string().min(10, { message: "Please enter a valid phone number." }),
  patientName: z.string().min(2, { message: "Patient name must be at least 2 characters." }),
  patientPhone: z.string().min(10, { message: "Please enter a valid phone number." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  date: z.date({
    required_error: "Please select a preferred appointment date.",
  }),
  timeSlot: z.string({
    required_error: "Please select a preferred time slot.",
  }),
  treatments: z.array(z.string()).min(1, { 
    message: "Please select at least one treatment." 
  }),
  message: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export function DentalAppointmentForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Define form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      bookingPersonName: "",
      bookingPersonPhone: "",
      patientName: "",
      patientPhone: "",
      email: "",
      timeSlot: "",
      treatments: [],
      message: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    
    try {
      // Format the date
      const formattedDate = format(data.date, "dd-MM-yyyy");
      
      // Get the time slot label
      const timeSlotLabel = timeSlots.find(slot => slot.id === data.timeSlot)?.label || data.timeSlot;
      
      // Format the treatments list
      const treatmentsList = data.treatments
        .map(id => treatmentOptions.find(t => t.id === id)?.label)
        .filter(Boolean)
        .join(", ");
      
      // Send the booking to the clinic on WhatsApp (no email)
      const whatsappMessage = `🦷 *New Appointment Request – Asrani Dental* 🦷
Booked by: ${data.bookingPersonName} (${data.bookingPersonPhone})
Patient: ${data.patientName} (${data.patientPhone})
Email: ${data.email}
Date: ${formattedDate} | Time: ${timeSlotLabel}
Treatments: ${treatmentsList}${data.message ? `\nMessage: ${data.message}` : ""}`;

      const clinicWhatsappUrl = `https://wa.me/918010224989?text=${encodeURIComponent(whatsappMessage)}`;
      const win = window.open(clinicWhatsappUrl, "_blank");
      if (!win) window.location.href = clinicWhatsappUrl;
      
      // Show success state
      setIsSubmitted(true);
      
      toast({
        title: "Appointment Booked Successfully",
        description: "Send the WhatsApp message to confirm your request.",
      });
      
      form.reset();
      
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: "There was a problem submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Get today's date to use as min value for date input
  const today = new Date();

  return (
    <div className="max-w-2xl w-full mx-auto">
      {isSubmitted ? (
        <div className="text-center p-8 bg-dental-highlight/10 rounded-xl">
          <div className="flex justify-center mb-4">
            <div className="bg-dental-highlight text-white rounded-full p-3">
              <Check className="h-8 w-8" />
            </div>
          </div>
          <h3 className="text-2xl font-serif font-semibold mb-3">Almost done!</h3>
          <p className="text-dental-muted mb-6">
            Thank you for booking an appointment with Asrani Dental Clinic. 
            Please press Send in WhatsApp so our team receives your request.
          </p>
          <Button 
            onClick={() => setIsSubmitted(false)}
            variant="outline"
          >
            Book Another Appointment
          </Button>
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="bg-dental-offwhite p-5 rounded-lg">
              <h3 className="text-lg font-medium mb-4">Booking Person Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="bookingPersonName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Full Name*
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bookingPersonPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        Phone Number*
                      </FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="+91 XXXXXXXXXX" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="bg-dental-offwhite p-5 rounded-lg">
              <h3 className="text-lg font-medium mb-4">Patient Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="patientName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Patient Name*
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Patient Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="patientPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        Patient Phone*
                      </FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="+91 XXXXXXXXXX" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Email Address*
                      </FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="johndoe@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="bg-dental-offwhite p-5 rounded-lg">
              <h3 className="text-lg font-medium mb-4">Appointment Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4" />
                        Appointment Date*
                      </FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarDays className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) => date < today}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="timeSlot"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Time Slot*
                      </FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a time slot" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {timeSlots.map((slot) => (
                            <SelectItem key={slot.id} value={slot.id}>
                              {slot.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="bg-dental-offwhite p-5 rounded-lg">
              <FormField
                control={form.control}
                name="treatments"
                render={() => (
                  <FormItem>
                    <div className="mb-4">
                      <FormLabel className="flex items-center gap-2">
                        Treatments*
                      </FormLabel>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {treatmentOptions.map((treatment) => (
                        <FormField
                          key={treatment.id}
                          control={form.control}
                          name="treatments"
                          render={({ field }) => {
                            return (
                              <FormItem
                                key={treatment.id}
                                className="flex flex-row items-start space-x-2 space-y-0"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(treatment.id)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([...field.value, treatment.id])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value) => value !== treatment.id
                                            )
                                          )
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="cursor-pointer font-normal">
                                  {treatment.label}
                                </FormLabel>
                              </FormItem>
                            )
                          }}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem className="bg-dental-offwhite p-5 rounded-lg">
                  <FormLabel className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Additional Message (Optional)
                  </FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Any specific concerns or information about the appointment..."
                      className="resize-none min-h-[100px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="pt-2">
              <Button 
                type="submit" 
                className="w-full bg-dental-highlight hover:bg-dental-accent transition-colors"
                disabled={isSubmitting}
              >
                <Send className="mr-2 h-4 w-4" /> {isSubmitting ? "Submitting..." : "Book Appointment"}
              </Button>
            </div>

            <div className="mt-4 text-xs text-center text-dental-muted">
              <p>By submitting this form, you agree to be contacted regarding your appointment request.</p>
            </div>
          </form>
        </Form>
      )}
    </div>
  );
}
